import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, ArrowLeftRight, SkipBack, SkipForward } from "lucide-react";
import { ArtworkPlaceholder } from "@/components/ArtworkPlaceholder";

function formatTime(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

export function MiniPlayer({ hidden }) {
  const { current, playing, toggle, progress, duration, seek, abMode, abSide, switchAB, stopAB, prevTrack, nextTrack, queue, queueIndex, openFullPlayer } = usePlayer();
  if (!current || hidden) return null;
  const hasQueue = queue?.length > 1 && queueIndex >= 0;
  return (
    <div
      className="hq-mini-pos fixed left-0 md:left-64 right-0 z-40 px-3 md:px-6 pointer-events-none"
      data-testid="mini-player"
    >
      <div className="pointer-events-auto w-full min-w-0 mx-auto max-w-3xl bg-black/80 backdrop-blur-2xl border border-white/10 rounded-2xl px-2.5 py-2.5 md:px-4 md:py-3 flex items-center gap-2 md:gap-3 shadow-[0_10px_40px_rgba(0,0,0,0.7)] overflow-hidden">
        <button type="button" onClick={() => openFullPlayer?.()} data-testid="mini-player-expand"
                className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-1 text-left">
          <ArtworkPlaceholder title={current.song?.name || ""} artworkId={current.song?.artwork_id} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-medium text-white" data-testid="mini-player-song">{current.song?.name || "—"}</div>
              <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-violet-600/20 text-violet-300 shrink-0 max-w-[30%] truncate">{current.version?.label}</span>
            </div>
            <div className="flex items-center gap-1.5 min-w-0 mt-1">
              <span className="mono text-[10px] text-zinc-500 tabular w-7 shrink-0 text-right">{formatTime(progress)}</span>
              <input
                type="range" min={0} max={duration || 0} step="0.1" value={progress}
                onChange={(e) => seek(parseFloat(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-0 w-full h-1 accent-violet-500"
                data-testid="mini-player-seek"
              />
              <span className="mono text-[10px] text-zinc-500 tabular w-7 shrink-0">{formatTime(duration)}</span>
            </div>
          </div>
        </button>
        {abMode && (
          <>
            <button onClick={switchAB} title="Switch A/B" data-testid="mini-player-ab"
              className="w-8 h-8 shrink-0 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-xs font-bold text-violet-300">
              {abSide}
            </button>
            <button onClick={stopAB} title="Exit A/B" data-testid="mini-player-ab-stop"
              className="w-8 h-8 shrink-0 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400">
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        <button onClick={prevTrack} data-testid="mini-player-prev"
          className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center transition-colors ${hasQueue ? "bg-white/5 hover:bg-white/10 text-white" : "bg-white/[0.02] text-zinc-600"}`}>
          <SkipBack className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={toggle} data-testid="mini-player-toggle"
          className="w-10 h-10 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center text-white transition-colors shrink-0"
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <button onClick={nextTrack} data-testid="mini-player-next"
          className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center transition-colors ${hasQueue && queueIndex + 1 < queue.length ? "bg-white/5 hover:bg-white/10 text-white" : "bg-white/[0.02] text-zinc-600"}`}>
          <SkipForward className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
